// src/components/common/vibe/CommentsSection.js
import React, { useState, useEffect, useRef } from 'react';
import { Send, Trash2, AtSign } from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import vibeService from '../../../services/vibe.service';
import { formatTimeAgo } from '../../../constants/vibeConstants';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';

const CommentsSection = ({ postId, currentUser, onCommentAdded, theme }) => {
    const { employees } = useEmployees();

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // @Mentions state
    const [mentionedEmployees, setMentionedEmployees] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const inputRef = useRef(null);

    // GIF state
    const [selectedGif, setSelectedGif] = useState(null);

    useEffect(() => {
        loadComments();
    }, [postId]);

    // Filter employees for mention dropdown
    const filteredEmployees = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(mentionSearch.toLowerCase())
    );

    const loadComments = async () => {
        try {
            const response = await vibeService.getComments(postId);
            setComments(response.data || []);
        } catch (error) {
            console.error('Error loading comments:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle text change with @mention detection
    const handleTextChange = (e) => {
        const text = e.target.value;
        setNewComment(text);

        // Detect @ mention
        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = text.substring(0, cursorPosition);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        if (lastWord.startsWith('@') && lastWord.length > 1) {
            setShowMentionDropdown(true);
            setMentionSearch(lastWord.substring(1));
        } else {
            setShowMentionDropdown(false);
        }
    };

    // Handle mention selection
    const handleMentionSelect = (employee) => {
        const cursorPosition = inputRef.current.selectionStart;
        const textBeforeCursor = newComment.substring(0, cursorPosition);
        const textAfterCursor = newComment.substring(cursorPosition);

        // Replace the @search with @FirstLast (no space for comments to match backend regex)
        const words = textBeforeCursor.split(/\s/);
        words[words.length - 1] = `@${employee.first_name}${employee.last_name}`;
        const newText = words.join(' ') + ' ' + textAfterCursor;

        setNewComment(newText);
        setShowMentionDropdown(false);

        // Add to mentioned employees if not already added
        if (!mentionedEmployees.includes(employee.id)) {
            setMentionedEmployees([...mentionedEmployees, employee.id]);
        }

        // Focus back on input
        inputRef.current.focus();
    };

    // Handle emoji selection
    const handleEmojiSelect = (emoji) => {
        const cursorPosition = inputRef.current.selectionStart || newComment.length;
        const textBeforeCursor = newComment.substring(0, cursorPosition);
        const textAfterCursor = newComment.substring(cursorPosition);
        const newText = textBeforeCursor + emoji + textAfterCursor;
        setNewComment(newText);
        inputRef.current.focus();
    };

    // Handle GIF selection
    const handleGifSelect = (gif) => {
        setSelectedGif(gif);
    };

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() && !selectedGif) return;

        setSubmitting(true);
        const commentText = newComment.trim();
        const commentData = { content: commentText };

        // Add GIF if selected
        if (selectedGif) {
            commentData.gif_url = selectedGif.url;
            commentData.gif_title = selectedGif.title;
        }

        try {
            // Create the comment
            const response = await vibeService.createComment(postId, commentData);

            // Clear input immediately for better UX
            setNewComment('');
            setSelectedGif(null);
            setMentionedEmployees([]);

            // Refresh comments to show the new one
            await loadComments();

            // Notify parent component
            if (onCommentAdded) onCommentAdded();

            //console.log('✅ Comment created successfully:', response);
        } catch (error) {
            console.error('❌ Error creating comment:', error);

            // Show user-friendly error message
            alert('Failed to post comment. Please try again.');

            // Restore the comment text so user doesn't lose it
            setNewComment(commentText);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Delete this comment?')) return;

        try {
            await vibeService.deleteComment(commentId);
            await loadComments();
            if (onCommentAdded) onCommentAdded();
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    return (
        <div className={`mt-4 pt-4 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
            {/* Comment Input */}
            <form onSubmit={handleSubmitComment} className="mb-4">
                <div className="relative">
                    {/* GIF Preview */}
                    {selectedGif && (
                        <div className={`mb-2 p-2 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex items-start gap-2">
                                <img
                                    src={selectedGif.url}
                                    alt={selectedGif.title}
                                    className="w-20 h-20 rounded object-cover"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs truncate ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        {selectedGif.title || 'Selected GIF'}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedGif(null)}
                                        className="text-xs text-red-600 hover:text-red-700 mt-1"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={newComment}
                                onChange={handleTextChange}
                                placeholder="Write a comment... (use @name to mention)"
                                className={`w-full px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />

                            {/* Mention Dropdown */}
                            {showMentionDropdown && filteredEmployees.length > 0 && (
                                <div className={`absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border shadow-lg ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600'
                                        : 'bg-white border-gray-200'
                                }`}>
                                    {filteredEmployees.slice(0, 5).map((emp) => (
                                        <button
                                            key={emp.id}
                                            type="button"
                                            onClick={() => handleMentionSelect(emp)}
                                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}
                                        >
                                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                                {emp.first_name[0]}{emp.last_name[0]}
                                            </div>
                                            <div>
                                                <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                                                <div className="text-xs opacity-75">{emp.designation}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Emoji Picker */}
                        <EmojiPicker
                            onEmojiSelect={handleEmojiSelect}
                            theme={theme}
                            position="top"
                        />

                        {/* GIF Picker */}
                        <GifPicker
                            onGifSelect={handleGifSelect}
                            theme={theme}
                        />

                        <button
                            type="button"
                            onClick={() => {
                                setNewComment(newComment + '@');
                                inputRef.current?.focus();
                            }}
                            className={`px-3 py-2 rounded-lg transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                            title="Mention someone"
                        >
                            <AtSign className="w-4 h-4" />
                        </button>

                        <button
                            type="submit"
                            disabled={(!newComment.trim() && !selectedGif) || submitting}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                (newComment.trim() || selectedGif) && !submitting
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : theme === 'dark'
                                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Mentioned Employees Display */}
                    {mentionedEmployees.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                            <span className={`text-xs font-medium ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Mentioning:
                            </span>
                            {mentionedEmployees.map((empId) => {
                                const emp = employees.find(e => e.id === empId);
                                if (!emp) return null;
                                return (
                                    <span
                                        key={empId}
                                        className={`px-2 py-1 rounded-full text-xs ${
                                            theme === 'dark'
                                                ? 'bg-blue-900/30 text-blue-400'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}
                                    >
                                        @{emp.first_name}{emp.last_name}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            </form>

            {/* Comments List */}
            {loading ? (
                <div className="text-center py-4">
                    <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : comments.length === 0 ? (
                <p className={`text-sm text-center py-4 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                    No comments yet. Be the first to comment!
                </p>
            ) : (
                <div className="space-y-3">
                    {comments.map((comment) => (
                        <div
                            key={comment.id}
                            className={`p-3 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                            }`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-sm font-semibold ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {comment.author_name}
                                        </span>
                                        <span className={`text-xs ${
                                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                        }`}>
                                            {formatTimeAgo(comment.created_at)}
                                        </span>
                                        {comment.is_edited && (
                                            <span className={`text-xs ${
                                                theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                            }`}>
                                                (edited)
                                            </span>
                                        )}
                                    </div>
                                    {(() => {
                                        // Parse content to extract text and GIF data
                                        let textContent = comment.content || '';
                                        let gifData = null;

                                        try {
                                            // Check if content contains JSON GIF data
                                            const lines = textContent.split('\n\n');
                                            const lastLine = lines[lines.length - 1];
                                            const parsedGif = JSON.parse(lastLine);

                                            if (parsedGif.type === 'gif' && parsedGif.url) {
                                                gifData = parsedGif;
                                                // Remove the JSON part from text content
                                                textContent = lines.slice(0, -1).join('\n\n');
                                            }
                                        } catch (e) {
                                            // Content is not JSON or doesn't contain GIF data
                                            // Keep original content
                                        }

                                        return (
                                            <>
                                                {/* Display text content */}
                                                {textContent && (
                                                    <p className={`text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>
                                                        {textContent}
                                                    </p>
                                                )}

                                                {/* Display GIF if present */}
                                                {gifData && (
                                                    <div className="mt-2">
                                                        <img
                                                            src={gifData.url}
                                                            alt={gifData.title || 'GIF'}
                                                            className="max-w-xs rounded"
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>

                                {comment.employee_id === currentUser?.id && (
                                    <button
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className={`p-1 rounded transition-colors ${
                                            theme === 'dark'
                                                ? 'hover:bg-gray-600 text-gray-400'
                                                : 'hover:bg-gray-200 text-gray-600'
                                        }`}
                                        title="Delete comment"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CommentsSection;
